import React, { useCallback, useEffect, useState } from 'react';
import { Card, Form, Input, Select, Table, Space, Button, message, Pagination, Upload, Typography, Tag } from 'antd';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { UploadOutlined } from '@ant-design/icons';
import { DebtTypeOptions, labelOfDebtType, toSelectOptions } from '../../constants/debtEnums';
import { getNoticeTemplates, uploadBusinessFile, saveNoticeTemplate, deleteNoticeTemplate } from '../../services/debt';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../config/theme';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import './NoticeTemplatePage.css';

const { Text } = Typography;

// 模板类型选项
const TEMPLATE_TYPE_OPTIONS = [
  { label: '全部', value: null },
  { label: '系统模板', value: 'SYSTEM' },
  { label: '个人模板', value: 'PERSONAL' },
];

// 判断用户是否为管理员
// roles 是字符串数组，如 ['PLATFORM_ADMIN', 'SUPER_ADMIN']
const isAdminUser = (roles) => {
  return roles && (roles.includes('PLATFORM_ADMIN') || roles.includes('SUPER_ADMIN') || roles.includes('ADMIN'));
};

const NoticeTemplatePage = () => {
  const { user, roles } = useSelector(state => state.auth);
  const { isMobile } = useBreakpoint();
  const [filterForm] = Form.useForm();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const normalizePageSize = useCallback(
    (size) => (PAGINATION_ENABLE_SIZE_CHANGER ? Number(size || PAGINATION_DEFAULT_PAGE_SIZE) : PAGINATION_DEFAULT_PAGE_SIZE),
    [],
  );
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGINATION_DEFAULT_PAGE_SIZE, total: 0 });
  const [sorter, setSorter] = useState({ field: 'update_time', order: 'desc' });
  const [name, setName] = useState('');
  const [modelType, setModelType] = useState(null);
  const [description, setDescription] = useState('');
  const [tempUrl, setTempUrl] = useState('');
  const [tempFileName, setTempFileName] = useState('');
  const [tempUploading, setTempUploading] = useState(false);
  const [templateType, setTemplateType] = useState(null); // 模板类型筛选

  // 判断当前用户是否为管理员
  const isAdmin = isAdminUser(roles);

  const fetchTemplates = useCallback(async (pageCfg, sortCfg) => {
    try {
      setLoading(true);
      const values = filterForm.getFieldsValue();
      const params = {
        page: pageCfg.current,
        size: pageCfg.pageSize,
        keyword: values.keyword,
        model_type: values.model_type,
        template_type: templateType, // 添加模板类型参数
        sort_field: sortCfg.field,
        sort_order: sortCfg.order,
        need_total: pageCfg.current === 1,
      };
      const { data } = await getNoticeTemplates(params);
      const list = Array.isArray(data?.records) ? data.records : [];
      setRecords(list);
      const totalFromApi = data?.total;
      setPagination({
        current: Number(data?.current || pageCfg.current),
        pageSize: normalizePageSize(data?.size || pageCfg.pageSize),
        total: Number(totalFromApi ?? (pageCfg.total ?? 0)),
      });
    } catch (e) {
      message.error('加载模板列表失败');
    } finally {
      setLoading(false);
    }
  }, [filterForm, normalizePageSize, templateType]);

  useEffect(() => {
    fetchTemplates(
      { current: 1, pageSize: PAGINATION_DEFAULT_PAGE_SIZE, total: 0 },
      { field: 'update_time', order: 'desc' },
    );
  }, [fetchTemplates]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchTemplates({ current: 1, pageSize: pagination.pageSize, total: pagination.total }, sorter);
  };

  const handlePaginationChange = (current, pageSize) => {
    const nextPageSize = normalizePageSize(pageSize);
    const next = {
      current: pageSize !== pagination.pageSize ? 1 : current,
      pageSize: nextPageSize,
      total: pagination.total,
    };
    setPagination(next);
    fetchTemplates(next, sorter);
  };

  const handleReset = () => {
    filterForm.resetFields();
    setTemplateType(null); // 重置模板类型筛选
    setPagination(prev => ({ ...prev, current: 1 }));
    setSorter({ field: 'update_time', order: 'desc' });
    fetchTemplates(
      { current: 1, pageSize: pagination.pageSize, total: pagination.total },
      { field: 'update_time', order: 'desc' },
    );
  };

  const uploadTempFile = async (file) => {
    if (!file) return;
    try {
      setTempUploading(true);
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!['doc', 'docx'].includes(ext)) {
        message.warning('仅支持上传Word文档（.doc/.docx）');
        return;
      }
      const businessId = user?.id || 0;
      const res = await uploadBusinessFile(file, 'notice_template_temp', businessId);
      setTempUrl(res?.data?.url || '');
      setTempFileName(file.name);
      const baseName = file.name.replace(/\.[^.]+$/, '');
      setName(baseName);
      setDescription(file.name);
      message.success('上传成功');
    } catch (_) {
      message.error('上传失败');
      throw _;
    } finally {
      setTempUploading(false);
    }
  };

  const saveTemplateAction = async () => {
    if (!name || !modelType || !tempUrl || !tempFileName) {
      message.warning('请填写名称、业务类型并上传文件');
      return;
    }
    try {
      const body = { name, model_type: modelType, description, temp_url: tempUrl, file_name: tempFileName };
      await saveNoticeTemplate(body);
      message.success('保存成功');
      setName('');
      setModelType(null);
      setDescription('');
      setTempUrl('');
      setTempFileName('');
      fetchTemplates({ current: 1, pageSize: pagination.pageSize, total: pagination.total }, sorter);
    } catch (_) {
      message.error('保存失败');
    }
  };

  const downloadFile = (record) => {
    const url = record.file_url || record.fileUrl || '';
    if (!url) {
      message.info('暂无文件');
      return;
    }
    window.open(url, '_blank');
  };

  const removeTemplate = async (record) => {
    try {
      await deleteNoticeTemplate(record.id);
      message.success('删除成功');
      fetchTemplates(pagination, sorter);
    } catch (_) {
      message.error('删除失败');
    }
  };

  const handleTableChange = (_pag, _filters, sorterArg) => {
    const order = sorterArg?.order ? (sorterArg.order === 'ascend' ? 'asc' : 'desc') : sorter.order;
    const field = sorterArg?.field || sorter.field;
    const nextSorter = { field, order };
    setSorter(nextSorter);
    fetchTemplates(pagination, nextSorter);
  };

  const columns = [
    { title: '模板名称', dataIndex: 'name', key: 'name', ellipsis: true },
    { title: '业务类型', dataIndex: 'model_type', key: 'model_type', width: 140, render: (v, r) => labelOfDebtType(v || r.modelType || '') },
    { title: '类型', dataIndex: 'template_type', key: 'template_type', width: 100, render: (v, r) => {
      const type = v || r.templateType;
      const isSystem = type === 'SYSTEM';
      return isSystem ? <Tag color="blue">系统</Tag> : <Tag color="green">个人</Tag>;
    }},
    { title: '说明', dataIndex: 'description', key: 'description', ellipsis: true },
    { title: '发布时间', dataIndex: 'publish_time', key: 'publish_time', width: 160, sorter: true, render: (v, r) => {
      const t = r.publish_time || r.publishTime;
      return t ? dayjs(t).format('YYYY-MM-DD HH:mm') : '';
    } },
    { title: '浏览量', dataIndex: 'views', key: 'views', width: 100 },
    { title: '下载量', dataIndex: 'downloads', key: 'downloads', width: 100 },
    { title: '操作', key: 'action', width: 120, render: (_, record) => {
      const templateType = record.template_type || record.templateType;
      const createBy = record.create_by || record.createBy;
      const isOwner = createBy === user?.id;
      // 管理员可以删除所有模板，普通用户只能删除自己的个人模板
      const canDelete = isAdmin || (isOwner && templateType === 'PERSONAL');
      return (
        <Space>
          <Button type="link" onClick={() => downloadFile(record)}>下载</Button>
          {canDelete && <Button type="link" danger onClick={() => removeTemplate(record)}>删除</Button>}
        </Space>
      );
    }},
  ];

  return (
    <div className="page-container p-debt-notice-template">
      <div className="page-content page-content-wide space-y-md">
        <div className="page-title">催告模板</div>

        <Card className="card-compact">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap>
              <Upload
                accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                showUploadList={false}
                customRequest={async ({ file, onSuccess, onError }) => {
                  try {
                    await uploadTempFile(file);
                    onSuccess?.(null);
                  } catch (err) {
                    onError?.(err);
                  }
                }}
              >
                <Button icon={<UploadOutlined />} loading={tempUploading}>
                  上传模板文件
                </Button>
              </Upload>
              <Text type="secondary">{tempFileName || '未选择文件'}</Text>
            </Space>

            <Space wrap style={{ width: '100%' }}>
              <Input
                placeholder="模板名称"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ width: isMobile ? '100%' : 220 }}
              />
              <Select
                placeholder="模板类型"
                value={modelType}
                onChange={setModelType}
                style={{ width: isMobile ? '100%' : 220 }}
                options={toSelectOptions(DebtTypeOptions)}
                allowClear
              />
              <Input
                placeholder="说明"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ width: isMobile ? '100%' : 320 }}
              />
              <Button type="primary" onClick={saveTemplateAction}>
                保存模板
              </Button>
            </Space>
          </Space>
        </Card>

        <Card className="card-compact">
          <Form layout={isMobile ? 'vertical' : 'inline'} form={filterForm} onFinish={handleSearch}>
            <Form.Item name="keyword" label="关键字">
              <Input placeholder="请输入模板名称/说明" allowClear style={{ width: isMobile ? '100%' : 240 }} />
            </Form.Item>
            <Form.Item name="model_type" label="业务类型">
              <Select allowClear style={{ width: isMobile ? '100%' : 180 }} options={toSelectOptions(DebtTypeOptions)} />
            </Form.Item>
            <Form.Item label="模板类型">
              <Select
                value={templateType}
                onChange={setTemplateType}
                options={TEMPLATE_TYPE_OPTIONS}
                style={{ width: isMobile ? '100%' : 140 }}
              />
            </Form.Item>
            <Form.Item>
              <Space wrap>
                <Button type="primary" htmlType="submit">查询</Button>
                <Button onClick={handleReset}>重置</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        <Card className="card-compact">
          <Table
            rowKey={(r) => r.id}
            loading={loading}
            dataSource={records}
            columns={columns}
            size={isMobile ? 'small' : 'middle'}
            pagination={false}
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
          />
          <div className="c-pagination-bar">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              simple={isMobile}
              showSizeChanger={isMobile ? false : PAGINATION_ENABLE_SIZE_CHANGER}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              onChange={handlePaginationChange}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default NoticeTemplatePage;
